import { useQuery } from '@tanstack/react-query'

import { assignmentsApi } from '@/services/assignments'
import { jobsApi } from '@/services/jobs'

/** Assignments of one job, enough for any staffing size shown on the page. */
const JOB_ASSIGNMENTS_PAGE_SIZE = 100

export const jobDetailQueryKey = (jobId: string) => ['scheduling-job-detail', jobId] as const
export const jobAssignmentsQueryKey = (jobId: string) => ['scheduling-job-assignments', jobId] as const

/**
 * Job detail (status, category id) and assignments for the expanded row. Both requests pass the
 * query signal, so closing the panel (unmount) aborts them if still in flight.
 */
export function useJobPanelData(jobId: string) {
  const job = useQuery({
    queryKey: jobDetailQueryKey(jobId),
    queryFn: ({ signal }) => jobsApi.getJob(jobId, { signal }),
    retry: false,
  })
  const assignments = useQuery({
    queryKey: jobAssignmentsQueryKey(jobId),
    queryFn: ({ signal }) => assignmentsApi
      .getAssignments({ job_id: jobId, page_size: JOB_ASSIGNMENTS_PAGE_SIZE }, { signal })
      .then((page) => page?.results ?? []),
    retry: false,
  })
  return { job, assignments }
}

/** Job detail already loaded by an expanded panel (no request of its own). */
export function useCachedJobDetail(jobId: string) {
  return useQuery({
    queryKey: jobDetailQueryKey(jobId),
    queryFn: ({ signal }) => jobsApi.getJob(jobId, { signal }),
    enabled: false,
  }).data
}
